/**
 * Everything the dashboard draws, read from the Sheet in one pass: the tables
 * the analysis takes, the Snapshot computed from them, the classified ledger
 * behind the spending figures, and the list of past reviews.
 */
import { analyse, ledger, type LedgerRow, type Snapshot } from './analysis'
import { catalog, type Catalog } from './categories'
import { toRule, type Rule } from './corrections'
import { tiller, type TillerReport } from './tiller'
import { withoutUnfinished, mergeSplitAccounts } from './duplicates'
import type { Database, Row } from './google/sheets'

export interface Tables {
  accounts: Row[]
  balances: Row[]
  transactions: Row[]
  holdings: Row[]
  documents: Row[]
  manual: Row[]
  corrections: Row[]
  rules: Row[]
  categories: Row[]
}

export interface Book {
  tables: Tables
  snapshot: Snapshot
  ledger: LedgerRow[]
  rules: Rule[]
  /** The Categories tab: names, parents and types. */
  categories: Catalog
  /** The Analyses tab, oldest first. */
  analyses: Row[]
  /** The Usage tab: one row per Claude call. */
  usage: Row[]
  /** The Profile tab, for the targets the charts draw against. */
  profile: Row[]
  /** What a linked Tiller sheet added this read, or why it couldn't be read. */
  tiller?: { report?: TillerReport; error?: string }
}

export async function readTables(db: Database): Promise<Tables> {
  const t = await db.readMany(['Accounts', 'Balances', 'Transactions', 'Holdings', 'Documents', 'Manual', 'Corrections', 'Rules', 'Categories'] as const)
  return tablesFrom(t)
}

async function tablesFrom(t: Record<'Accounts' | 'Balances' | 'Transactions' | 'Holdings' | 'Documents' | 'Manual' | 'Corrections' | 'Rules' | 'Categories', { rows: Row[] }>): Promise<Tables> {
  const { Accounts: accounts, Balances: balances, Transactions: transactions, Holdings: holdings, Documents: documents, Manual: manual, Corrections: corrections, Rules: rules, Categories: categories } = t
  // A linked Tiller sheet adds rows in memory only; see tiller.ts.
  return tiller.merge(
    withoutUnfinished(
      mergeSplitAccounts({
        accounts: accounts.rows,
        balances: balances.rows,
        transactions: transactions.rows,
        holdings: holdings.rows,
        documents: documents.rows,
        manual: manual.rows,
        corrections: corrections.rows,
        rules: rules.rows,
        categories: categories.rows,
      }),
    ),
  )
}

export function bookFrom(tables: Tables, analyses: Row[], usage: Row[] = [], profile: Row[] = []): Book {
  return {
    tables,
    snapshot: analyse(tables),
    ledger: ledger(tables),
    rules: tables.rules.map(toRule).filter((r) => r.match),
    categories: catalog(tables.categories),
    analyses,
    usage,
    profile,
  }
}

export async function readBook(db: Database): Promise<Book> {
  // One request for every tab the dashboard draws from.
  const t = await db.readMany(['Accounts', 'Balances', 'Transactions', 'Holdings', 'Documents', 'Manual', 'Corrections', 'Rules', 'Categories', 'Analyses', 'Usage', 'Profile'] as const)
  const tables = await tablesFrom(t)
  return { ...bookFrom(tables, t.Analyses.rows, t.Usage.rows, t.Profile.rows), tiller: { report: tiller.report(), error: tiller.error() } }
}
